import React, { useRef, useEffect } from 'react'
import { isApp } from '../utils/platform'
import { FLUTTER_GALLERY_ERROR_MESSAGE } from '../constants/flutterBridgeMessages'
import { toastUtil } from '../utils/toast'
import { dataURLtoFile } from '../utils/fileUtils'
import FormErrorMessage from './FormErrorMessage'

interface ProfileImageUploadProps {
  profileImagePreview: string | null
  error?: string
  onFileSelect: (file: File) => void
  onError?: (error: string) => void
}

const ProfileImageUpload: React.FC<ProfileImageUploadProps> = ({
  profileImagePreview,
  error,
  onFileSelect,
  onError,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Flutter 브리지 콜백 등록
  useEffect(() => {
    if (isApp() && (window as any).FlutterBridge) {
      const flutterBridge = (window as any).FlutterBridge

      flutterBridge.onGalleryError = (data: { message?: string }) => {
        toastUtil.error(data?.message ?? FLUTTER_GALLERY_ERROR_MESSAGE)
      }

      flutterBridge.onImagesSelected = (data: any) => {
        if (
          data &&
          data.images &&
          Array.isArray(data.images) &&
          data.images.length > 0
        ) {
          const img = data.images[0]
          const file = dataURLtoFile(img.data, img.name || 'profile-image.jpg')

          if (file.size > 5 * 1024 * 1024) {
            const errorMsg = '파일 크기는 5MB 이하여야 합니다'
            toastUtil.error(errorMsg)
            onError?.(errorMsg)
            return
          }

          onFileSelect(file)
          onError?.('')
        }
      }

      return () => {
        if (flutterBridge.onGalleryError) {
          delete flutterBridge.onGalleryError
        }
        if (flutterBridge.onImagesSelected) {
          delete flutterBridge.onImagesSelected
        }
      }
    }
  }, [onFileSelect, onError])

  const handlePhotoUpload = () => {
    // 앱 환경이면 Flutter 브리지 사용 (단일 이미지 선택)
    if (isApp() && (window as any).FlutterBridge) {
      ;(window as any).FlutterBridge.openGallerySingle()
    } else {
      // 웹 환경이면 기본 file input 사용
      fileInputRef.current?.click()
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        const errorMsg = '파일 크기는 5MB 이하여야 합니다'
        toastUtil.error(errorMsg)
        onError?.(errorMsg)
        return
      }

      if (!file.type.startsWith('image/')) {
        const errorMsg = '이미지 파일만 업로드 가능합니다'
        toastUtil.error(errorMsg)
        onError?.(errorMsg)
        return
      }

      onFileSelect(file)
      onError?.('')
    }
  }

  return (
    <div className="flex flex-col items-center mt-26px">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      <div
        className={`w-114px h-114px flex items-center rounded-full justify-center cursor-pointer hover:opacity-70
            ${error ? 'border-2 border-semanticError' : ''}
            ${profileImagePreview ? 'bg-brandPrimary-5% overflow-hidden' : ''}
        `}
        onClick={handlePhotoUpload}
      >
        {profileImagePreview ? (
          <img
            src={profileImagePreview}
            alt="프로필"
            className="w-full h-full object-cover"
          />
        ) : (
          <img
            src="/images/profile-placeholder-icon.png"
            alt="프로필"
            className="w-full h-full object-contain opacity-80"
          />
        )}
      </div>
      <FormErrorMessage error={error} isTextCenter />
    </div>
  )
}

export default ProfileImageUpload
